/**
 * Derivações puras da Tela 4 (Coleta Estruturada) — wire → view model.
 *
 * Concentra TODA decisão de "como exibir/validar o dado real" para ser testável
 * sem React. Honestidade é regra (memória `feedback_anotacoes_prototipo_nao_sao_copy_producao`):
 *   - D3: NÃO validamos valores (CPF/CEP) — só obrigatórios client-side; a
 *     Pendency de revisão humana é o mecanismo de confiança.
 *   - PR B (ADR-155 D9): o form deixa de ser FIXO — quando o Playbook declara
 *     `requiredFields`, a tela DERIVA deles (`schemaToFieldDefs`, seção única
 *     "Coleta"); sem declaração, o form legado de 9 campos (D8 byte-idêntico).
 *   - PATCH substitui `fields` inteiro no backend — por isso `fieldsToPayload`
 *     devolve o conjunto completo (descartando vazios) a cada save.
 */

import type { ColetaRouteError, ColetaSchema, ColetaStatus } from './types';

export type ColetaSection = 'identificacao' | 'endereco' | 'coleta';

export interface ColetaFieldDef {
  key: string;
  label: string;
  required: boolean;
  section: ColetaSection;
  placeholder?: string;
  /** largura relativa no grid (1 = inteira, 0.5 = meia). Default 1. */
  span?: number;
  /**
   * PR B — tag do DESTINO do campo quando `target≠person` (vínculo/episódio/
   * ERP…): o roteamento é auditável pelo operador antes do finalize (ADR-155).
   * F2-c: o termo é NEUTRO ("vínculo"), nunca "contrato" — o motor é agnóstico
   * de `bond.type` (contrato é só um dos 21).
   */
  tag?: string;
}

/**
 * Form fixo (D5) — fiel ao mockup `coleta-estruturada.app.jsx` (seções
 * Identificação + Endereço). NÃO inventamos campos "sugeridos pela IA".
 * `name`/`cpf` são as únicas chaves especiais no dispatcher do backend
 * (`buildPersonWritePlan` em `structured-collections/dispatch-plan.ts`,
 * ADR-155 Fase 3 — sucessor do antigo `mapFieldsToPerson`).
 */
export const COLETA_FIELDS: readonly ColetaFieldDef[] = [
  {
    key: 'name',
    label: 'Nome completo',
    required: true,
    section: 'identificacao',
    placeholder: 'Nome legal completo',
  },
  {
    key: 'cpf',
    label: 'CPF',
    required: true,
    section: 'identificacao',
    placeholder: '000.000.000-00',
  },
  {
    key: 'birthDate',
    label: 'Data de nascimento',
    required: false,
    section: 'identificacao',
    placeholder: 'dd/mm/aaaa',
  },
  {
    key: 'cep',
    label: 'CEP',
    required: true,
    section: 'endereco',
    placeholder: '00000-000',
    span: 0.5,
  },
  { key: 'uf', label: 'UF', required: false, section: 'endereco', placeholder: 'RS', span: 0.5 },
  {
    key: 'logradouro',
    label: 'Logradouro',
    required: true,
    section: 'endereco',
    placeholder: 'Rua, Av…',
  },
  { key: 'numero', label: 'Número', required: true, section: 'endereco', span: 0.5 },
  { key: 'bairro', label: 'Bairro', required: false, section: 'endereco', span: 0.5 },
  { key: 'cidade', label: 'Cidade', required: false, section: 'endereco' },
];

export function requiredFieldKeys(defs: readonly ColetaFieldDef[] = COLETA_FIELDS): string[] {
  return defs.filter((f) => f.required).map((f) => f.key);
}

const isFilled = (v: string | undefined): boolean => v !== undefined && v.trim() !== '';

/** % preenchido sobre os campos DEFINIDOS no form (chaves extra não inflam). */
export function progressOf(
  fields: Record<string, string>,
  defs: readonly ColetaFieldDef[] = COLETA_FIELDS,
): number {
  const total = defs.length;
  if (total === 0) return 0;
  const filled = defs.filter((f) => isFilled(fields[f.key])).length;
  return Math.round((filled / total) * 100);
}

/** Obrigatórios ainda vazios (espaço em branco conta como vazio). */
export function missingRequired(
  fields: Record<string, string>,
  defs: readonly ColetaFieldDef[] = COLETA_FIELDS,
): string[] {
  return requiredFieldKeys(defs).filter((k) => !isFilled(fields[k]));
}

export function isFinalizable(
  fields: Record<string, string>,
  defs: readonly ColetaFieldDef[] = COLETA_FIELDS,
): boolean {
  return missingRequired(fields, defs).length === 0;
}

// ─── PR B (ADR-155 D9) — modo dinâmico derivado do Playbook ─────────────────

const SECTION_LABELS: Record<ColetaSection, string> = {
  identificacao: 'Identificação do cliente',
  endereco: 'Endereço',
  coleta: 'Coleta',
};

/** Labels do form legado, reusados quando o Playbook declara a mesma chave. */
const KNOWN_LABELS: Record<string, string> = Object.fromEntries(
  COLETA_FIELDS.map((f) => [f.key, f.label]),
);

/**
 * Destino visível ao operador quando o campo NÃO grava na pessoa. F2-c:
 * NEUTRALIDADE dura — `bond` é "vínculo", jamais "contrato" (o motor é
 * agnóstico do `bond.type`; contrato é só um dentre os 21).
 */
const TARGET_TAG: Partial<Record<string, string>> = {
  bond: 'vínculo',
  'bond-participant': 'participante',
  'authorized-contact': 'autorizado',
  episode: 'episódio',
  'erp-reconcile': 'ERP',
};

const VALIDATE_PLACEHOLDER: Record<string, string> = {
  cpf: '000.000.000-00',
  cnpj: '00.000.000/0000-00',
};

/** `convenioMedico` / `convenio_medico` → "Convenio medico" (1ª maiúscula). */
function humanizeFieldName(name: string): string {
  const words = name
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .trim();
  return words.length > 0 ? words.charAt(0).toUpperCase() + words.slice(1) : name;
}

/**
 * Wire do schema → defs do form. `source:'default'` devolve o form legado
 * FIXO (MESMA referência — D8 byte-idêntico p/ tenant sem declaração);
 * `'playbook'` deriva defs dinâmicos em seção única "Coleta" (a UX fina
 * Coleta×Agenda é Fase 4 com Claude Design).
 */
export function schemaToFieldDefs(schema: ColetaSchema): readonly ColetaFieldDef[] {
  if (schema.source !== 'playbook' || schema.fields.length === 0) return COLETA_FIELDS;
  return schema.fields.map((f) => {
    const placeholder = f.validate !== null ? VALIDATE_PLACEHOLDER[f.validate] : undefined;
    const tag = TARGET_TAG[f.target];
    return {
      key: f.name,
      label: KNOWN_LABELS[f.name] ?? humanizeFieldName(f.name),
      required: f.required,
      section: 'coleta' as const,
      ...(placeholder !== undefined && { placeholder }),
      ...(tag !== undefined && { tag }),
    };
  });
}

/**
 * PR C (ADR-155 D5) — defaults herdados p/ pré-preencher campos VAZIOS
 * (só episodic; valor escalar coagido a string — objeto/array descartado).
 */
export function inheritedDefaults(schema: ColetaSchema): Record<string, string> {
  if (schema.source !== 'playbook') return {};
  const out: Record<string, string> = {};
  for (const f of schema.fields) {
    if (f.durability !== 'episodic' || f.inherited === null) continue;
    const v = f.inherited.value;
    if (typeof v === 'string') out[f.name] = v;
    else if (typeof v === 'number' || typeof v === 'boolean') out[f.name] = String(v);
  }
  return out;
}

export interface ColetaSectionView {
  section: ColetaSection;
  label: string;
  fields: ColetaFieldDef[];
}

/** Agrupa os defs por seção preservando a ordem de aparição. */
export function sectionsOf(defs: readonly ColetaFieldDef[]): ColetaSectionView[] {
  const out: ColetaSectionView[] = [];
  for (const def of defs) {
    const existing = out.find((s) => s.section === def.section);
    if (existing !== undefined) existing.fields.push(def);
    else out.push({ section: def.section, label: SECTION_LABELS[def.section], fields: [def] });
  }
  return out;
}

/**
 * Mensagem por-campo dos 422 de ROTEAMENTO do dispatcher (ADR-155 Fase 3 —
 * `collection_target_*`). Nomeia o campo pelo label do form; código
 * desconhecido degrada para o `reason` do backend (honesto).
 */
export function routeErrorMessage(err: ColetaRouteError, defs: readonly ColetaFieldDef[]): string {
  const label = defs.find((d) => d.key === err.field)?.label ?? err.field;
  switch (err.error) {
    case 'collection_target_context_missing':
      // F2-c: wording NEUTRO — "vínculo", nunca "contrato" (o motor é agnóstico
      // do bond.type). O foco do vínculo (activeBondId/Moldura F1) precisa estar
      // resolvido antes do finalize; a affordance é estabelecer/escolher o vínculo.
      return `“${label}” grava no vínculo, mas esta conversa não tem um vínculo em foco — estabeleça ou selecione o vínculo antes de finalizar.`;
    case 'collection_target_not_implemented':
      return `“${label}” declara um destino ainda não suportado pela coleta. Ajuste a declaração do campo no Playbook.`;
    case 'collection_target_episode_closed':
      return `“${label}” não pode ser gravado: o episódio vinculado à conversa já foi encerrado.`;
    default:
      return err.reason;
  }
}

/** Códigos de 422 do dispatcher que viram aviso persistente por-campo no painel. */
export function isRouteErrorCode(code: string): boolean {
  return (
    code === 'collection_target_context_missing' ||
    code === 'collection_target_not_implemented' ||
    code === 'collection_target_episode_closed'
  );
}

/**
 * Wire (`Record<string, unknown>`) → form state (`Record<string, string>`).
 * Coage valores a string, descarta null/undefined; preserva chaves extra
 * (metadata.collected pré-existente) para round-trip sem perda.
 */
export function fieldsFromDraft(raw: Record<string, unknown>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(raw)) {
    if (typeof v === 'string') out[k] = v;
    else if (typeof v === 'number' || typeof v === 'boolean') out[k] = String(v);
    // objetos/arrays/null não são valores de campo escalares — ignorados (honesto).
  }
  return out;
}

/**
 * Form state → payload `fields` do PATCH/POST. Descarta strings vazias (o PATCH
 * substitui `fields` inteiro — vazios apenas sujariam metadata.collected).
 */
export function fieldsToPayload(fields: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(fields)) {
    if (isFilled(v)) out[k] = v.trim();
  }
  return out;
}

const STATUS_LABEL: Record<ColetaStatus, string> = {
  in_progress: 'Em preenchimento',
  completed: 'Finalizada',
  cancelled: 'Cancelada',
  awaiting_confirmation: 'Aguardando confirmação do titular',
};

export function statusLabel(status: ColetaStatus): string {
  return STATUS_LABEL[status] ?? status;
}

/** "14:32" do updatedAt; ISO inválido/null → "—". */
export function lastSavedLabel(iso: string | null): string {
  if (iso === null || iso === '') return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' }).format(d);
}

const ERROR_MESSAGE: Record<string, string> = {
  structured_collection_empty: 'Preencha ao menos um campo antes de finalizar a coleta.',
  structured_collection_not_editable:
    'Esta coleta já foi finalizada ou cancelada e não pode mais ser editada.',
  // Item 361 F2 — confirmação com o titular.
  no_person: 'A conversa ainda não tem um titular identificado. Vincule a pessoa antes.',
  no_validated_channel:
    'O titular não tem nenhum canal validado para receber a confirmação. Use a validação documental (anexos) e a revisão humana.',
  bond_cadastral_frozen:
    'As alterações cadastrais deste vínculo estão congeladas até revisão humana (o titular negou um pedido de alteração).',
};

export function coletaErrorMessage(code: string): string {
  return ERROR_MESSAGE[code] ?? 'Não foi possível concluir a operação. Tente novamente.';
}
